/* cp-outubro-rosa-gift-v1. Canonical storefront source; no checkout/server enforcement. */
(() => {
  'use strict';
  if (globalThis.CPPapayaPinkGift) return;
  const IDS = Object.freeze({
    caseProduct: '8285250977957',
    strapProducts: Object.freeze(['8285151953061', '8285185573029', '8869390876837']),
    giftProduct: '10804626718885',
    giftVariant: '50587452801189'
  });
  const productId = (item) => String(item.product_id ?? item.product?.id ?? '');
  const variantId = (item) => String(item.variant_id ?? item.variant?.id ?? item.id ?? '');
  const isGift = (item) => productId(item) === IDS.giftProduct || variantId(item) === IDS.giftVariant;
  const positiveQuantity = (item) => Number.isSafeInteger(Number(item.quantity)) && Number(item.quantity) > 0;
  const isPaid = (item) => Number(item.final_line_price ?? item.line_price ?? item.final_price ?? item.price ?? 0) > 0;

  function planGift(cart) {
    if (!Array.isArray(cart?.items)) throw new Error('Carrinho inválido.');
    let hasCase = false, hasStrap = false;
    const inspect = (item, paidParent = false) => {
      if (!positiveQuantity(item) || isGift(item)) return;
      const paid = paidParent || isPaid(item);
      if (paid && productId(item) === IDS.caseProduct) hasCase = true;
      if (paid && IDS.strapProducts.includes(productId(item))) hasStrap = true;
      // Native bundles expose their component products; the paid parent establishes payment.
      for (const component of item.item_components || []) inspect(component, paid);
    };
    cart.items.forEach((item) => inspect(item));
    const eligible = hasCase && hasStrap;
    const gifts = cart.items.filter((item) => positiveQuantity(item) && isGift(item));
    const keeper = eligible ? gifts.find((item) => variantId(item) === IDS.giftVariant) : null;
    const updates = {};
    for (const item of gifts) {
      const quantity = item === keeper ? 1 : 0;
      if (Number(item.quantity) !== quantity) {
        if (!item.key) throw new Error('O brinde está sem identificador de carrinho.');
        updates[item.key] = quantity;
      }
    }
    return { eligible, hasCase, hasStrap, giftQuantity: gifts.reduce((sum, item) => sum + Number(item.quantity), 0),
      updates, add: eligible && !keeper ? { id: IDS.giftVariant, quantity: 1, properties: { _cp_outubro_rosa: '2026' } } : null };
  }

  function eligibilityFingerprint(cart) {
    return JSON.stringify(cart.items.filter((item) => !isGift(item)).map((item) =>
      [item.key, item.quantity, item.final_line_price ?? item.line_price, item.item_components || null]));
  }

  // One queue owns all observed cart writes and gift reconciliation. Adapters are testable without a DOM.
  function createCoordinator(adapter) {
    let tail = Promise.resolve(), pending = 0, lastCart = null, unavailableFingerprint = null;
    const enqueue = (work) => {
      pending += 1;
      const result = tail.then(work);
      tail = result.catch(() => {}).finally(() => { pending -= 1; });
      return result;
    };
    const read = async () => {
      lastCart = await adapter.readCart();
      planGift(lastCart);
      adapter.observed?.(lastCart);
      return lastCart;
    };
    const reconcile = async () => {
      let cart = await read(), changed = false, unavailable = false;
      for (let attempt = 0; attempt < 4; attempt += 1) {
        const plan = planGift(cart);
        if (Object.keys(plan.updates).length) {
          // Never send notes, attributes, or other cart lines in a gift-only update.
          try { await adapter.updateGift(plan.updates); }
          catch (error) {
            // A timed-out write may have succeeded; reconcile once, never blindly replay.
            cart = await read();
            if (Object.keys(planGift(cart).updates).length) throw error;
          }
          changed = true;
          cart = await read();
          continue;
        }
        if (!plan.add) return { cart, changed, unavailable };
        const fingerprint = eligibilityFingerprint(cart);
        if (unavailableFingerprint === fingerprint) return { cart, changed, unavailable: true };
        try {
          await adapter.addGift(plan.add);
          unavailableFingerprint = null;
          changed = true;
        } catch (error) {
          // Paid products remain purchased successfully when the promotional item is unavailable.
          cart = await read();
          const currentPlan = planGift(cart);
          if (currentPlan.add && !Object.keys(currentPlan.updates).length) {
            unavailableFingerprint = eligibilityFingerprint(cart);
            return { cart, changed, unavailable: true };
          }
          if (!currentPlan.eligible && currentPlan.giftQuantity === 0) return { cart, changed, unavailable: false };
          changed = true;
        }
        cart = await read();
      }
      throw new Error('Não foi possível ajustar o brinde. Confira o carrinho e tente novamente.');
    };
    return Object.freeze({
      sync: (finish = (result) => result) => enqueue(async () => finish(await reconcile())),
      mutate: (perform, finish = (result) => result) => enqueue(async () => {
        const response = await perform();
        // A failed/partially accepted cart write can still change Shopify's cart.
        let result;
        try { result = { response, ...(await reconcile()) }; }
        catch (error) { result = { response, cart: lastCart, error }; }
        return finish(result);
      }),
      checkout: (proceed) => enqueue(async () => {
        const result = await reconcile();
        if (Object.keys(planGift(result.cart).updates).length) throw new Error('Ajuste o brinde antes de finalizar.');
        // Invoke navigation inside the queue so a subsequent mutation cannot slip between check and submit.
        await proceed(result);
        return result;
      }),
      state: () => ({ pending, cart: lastCart }),
      retryGift: () => { unavailableFingerprint = null; }
    });
  }

  const api = { ids: IDS, planGift, createCoordinator };
  globalThis.CPPapayaPinkGift = api;
  if (typeof document === 'undefined') return;
  const root = window.Shopify?.routes?.root || '/';
  const rawFetch = window.fetch.bind(window);
  const cartPath = new URL(`${root}cart`, window.location.origin).pathname;
  let latestCart = null, latestSections = null, checkoutPending = false, replayingForm = null;
  let latestCartFingerprint = null;
  let initializedKeys = false;
  const errorId = 'cp-outubro-rosa-message';

  async function requestJson(url, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await rawFetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'Não foi possível conferir o brinde. Tente novamente.');
      return data;
    } finally { clearTimeout(timeout); }
  }
  const writeGift = (action, body) => requestJson(`${root}cart/${action}.js`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify(body)
  });
  const coordinator = createCoordinator({
    readCart: () => requestJson(`${root}cart.js`),
    updateGift: (updates) => writeGift('update', { updates }),
    addGift: (item) => writeGift('add', { items: [item] }),
    observed(cart) {
      const fingerprint = JSON.stringify(cart.items.map((item) => [item.key, item.quantity, item.final_line_price]));
      if (fingerprint !== latestCartFingerprint) latestSections = null;
      latestCartFingerprint = fingerprint;
      latestCart = cart;
      // Liquid data attributes are authoritative. This fallback serves older section markup on first load.
      if (!initializedKeys) { annotateKeys(document, cart); initializedKeys = true; }
    }
  });

  function annotateKeys(scope, cart) {
    for (const input of scope.querySelectorAll('line-item-quantity [data-line]')) {
      const control = input.closest('line-item-quantity');
      const item = cart.items[Number(input.dataset.line) - 1];
      if (item?.key && !control.dataset.cpCartLineKey) control.dataset.cpCartLineKey = item.key;
    }
  }
  function lineKeys() {
    const keys = {};
    for (const input of document.querySelectorAll('line-item-quantity [data-line]')) {
      const key = input.closest('line-item-quantity')?.dataset.cpCartLineKey;
      if (key) keys[input.dataset.line] = key;
    }
    return keys;
  }
  function showMessage(message, blocking = false) {
    let node = document.getElementById(errorId);
    if (!message) { node?.remove(); return; }
    if (!node) {
      node = document.createElement('div');
      node.id = errorId;
      node.className = 'notification notification--error';
      node.style.cssText = 'position:fixed;z-index:100000;left:16px;right:16px;bottom:16px;max-width:620px;margin:auto;padding:16px;background:#fff4ef;color:#652e36;border:1px solid #ddb7be;box-shadow:0 4px 18px #0002';
      document.body.append(node);
    }
    node.setAttribute('role', blocking ? 'alert' : 'status');
    node.textContent = message;
  }
  function report(result) {
    if (result.error) showMessage('Não foi possível ajustar o brinde. Confira o carrinho antes de finalizar.', true);
    else if (result.unavailable) showMessage('A Alça Rosa Brinde está indisponível no momento. Seus produtos continuam no carrinho.');
    else showMessage('');
  }
  function mainSection() {
    return document.querySelector('.cart__recap')?.closest('[id^="shopify-section-"]');
  }
  async function sectionsFor(cart) {
    const main = mainSection();
    const ids = ['mini-cart'];
    if (main) ids.push(main.id.replace('shopify-section-', ''));
    const sections = await requestJson(`${root}cart?sections=${encodeURIComponent(ids.join(','))}`);
    for (const id of ids) {
      if (typeof sections[id] !== 'string' || !sections[id].trim()) throw new Error('Não foi possível atualizar o carrinho.');
      const template = document.createElement('template');
      template.innerHTML = sections[id];
      annotateKeys(template.content, cart);
      sections[id] = template.innerHTML;
    }
    latestSections = sections;
    return sections;
  }
  async function render(result, refreshMain = false) {
    if (!result.cart) return;
    const cart = result.cart;
    const sections = await sectionsFor(cart);
    cart.sections = sections;
    if (refreshMain) {
      const main = mainSection();
      if (main) {
        const template = document.createElement('template');
        template.innerHTML = sections[main.id.replace('shopify-section-', '')];
        const replacement = template.content.querySelector(`[id="${main.id}"]`);
        if (replacement) main.replaceWith(replacement);
      }
    }
    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart, cpPinkGift: true } }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart, cpPinkGift: true } }));
  }

  async function keyedRequest(input, options, keys, action) {
    const request = new Request(input, options);
    if (action !== 'change') return request;
    const contentType = request.headers.get('Content-Type') || '';
    if (!contentType.includes('application/json')) return request;
    const body = await request.clone().json();
    if (body.line && !body.id && keys[body.line]) {
      body.id = keys[body.line];
      delete body.line;
      return new Request(request, { body: JSON.stringify(body) });
    }
    return request;
  }

  // Focal's cart page reloads immediately after a change response, without emitting cart events.
  // This limited adapter settles only same-origin cart writes before releasing that response.
  window.fetch = function (input, options) {
    let url;
    try { url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, window.location.href); }
    catch { return rawFetch(input, options); }
    const method = String(options?.method || input?.method || 'GET').toUpperCase();
    const action = ['add', 'change', 'update', 'clear'].find((name) => url.pathname === `${cartPath}/${name}.js`);
    if (url.origin !== window.location.origin || method !== 'POST' || !action) return rawFetch(input, options);
    const keys = lineKeys();
    // Convert relative inputs to absolute URLs for Request, preserving every other request option.
    const requestInput = typeof input === 'string' || input instanceof URL ? url.href : input;
    return coordinator.mutate(async () => rawFetch(await keyedRequest(requestInput, options, keys, action)), async (result) => {
      report(result);
      const response = result.response;
      if (!response.ok || !result.cart) return response;
      try {
        const payload = await response.clone().json();
        const sections = await sectionsFor(result.cart);
        const body = action === 'add' ? { ...payload, sections } : { ...result.cart, sections };
        const headers = new Headers(response.headers);
        headers.delete('content-length');
        headers.delete('content-encoding');
        return new Response(JSON.stringify(body), { status: response.status, statusText: response.statusText, headers });
      } catch {
        // Preserve the successful purchase request; a fresh cart page will reconcile on load.
        latestSections = null;
        showMessage('Carrinho atualizado. Abra o carrinho para conferir o brinde.');
        return response;
      }
    });
  };

  // Focal may emit a response it retained before a later write. Never render stale gift sections.
  const refreshCurrentCart = (event) => {
    if (!event.detail || !latestCart) return;
    if (event.type === 'cart:refresh') {
      // Focal delays removals by 600 ms and quantity edits by 750 ms. An older delayed
      // replacement can otherwise overwrite a newer gift result after a rapid add/remove.
      event.detail.replacementDelay = 0;
      const drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.nextReplacementDelay = 0;
    }
    if (event.detail.cpPinkGift) return;
    event.detail.cart = { ...latestCart };
    if (latestSections) event.detail.cart.sections = latestSections;
  };
  document.addEventListener('cart:updated', refreshCurrentCart, true);
  document.addEventListener('cart:refresh', refreshCurrentCart, true);

  async function standardCheckout(proceed) {
    if (checkoutPending) return;
    checkoutPending = true;
    try {
      await coordinator.checkout(async (result) => { report(result); await proceed(); });
    } catch {
      showMessage('Não foi possível conferir o brinde. Atualize o carrinho e tente finalizar novamente.', true);
    } finally { checkoutPending = false; }
  }
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (form === replayingForm || !(form instanceof HTMLFormElement)) return;
    const target = new URL(form.action, window.location.href);
    const checkout = event.submitter?.name === 'checkout' || Boolean(form.querySelector('input[name="checkout"]'));
    if (!checkout || target.origin !== window.location.origin || target.pathname.replace(/\/$/, '') !== cartPath) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const submitter = event.submitter;
    standardCheckout(() => {
      // Focal may replace the drawer while an earlier cart write is settling.
      // Resolve the live form again; submitting the detached original silently does nothing.
      const currentForm = form.isConnected ? form :
        (form.id ? document.getElementById(form.id) : document.querySelector('form.cart__recap'));
      if (!(currentForm instanceof HTMLFormElement) || !currentForm.isConnected) {
        throw new Error('Abra o carrinho e tente finalizar novamente.');
      }
      if (currentForm !== form) {
        for (const field of Array.from(form.elements)) {
          if (field.disabled || !(field.name === 'note' || /^attributes\[[^\]]+\]$/.test(field.name))) continue;
          let currentField = Array.from(currentForm.elements).find((candidate) => candidate.name === field.name);
          if (!currentField) {
            currentField = document.createElement('input');
            currentField.type = 'hidden';
            currentField.name = field.name;
            currentForm.append(currentField);
          }
          currentField.value = field.value;
          if ('checked' in field) currentField.checked = field.checked;
        }
      }
      // Gift insertion changes Shopify's line ordering: never post stale index-based updates[].
      // Note/attributes stay on the form; quantity controls are saved separately by Focal's cart API.
      const updates = Array.from(currentForm.elements).filter((field) => /^updates(?:\[|$)/.test(field.name) && !field.disabled);
      updates.forEach((field) => { field.disabled = true; });
      const currentSubmitter = submitter?.isConnected && submitter.form === currentForm ? submitter :
        Array.from(currentForm.elements).find((field) => field.name === 'checkout' && field.type === 'submit');
      replayingForm = currentForm;
      try { currentForm.requestSubmit(currentSubmitter); }
      finally { replayingForm = null; updates.forEach((field) => { field.disabled = false; }); }
    });
  }, true);
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[href]');
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin !== window.location.origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?checkout\/?$/i.test(url.pathname)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    standardCheckout(() => window.location.assign(url.href));
  }, true);

  api.sync = () => coordinator.sync(async (result) => {
    report(result);
    if (result.changed) await render(result, true);
    return result;
  });
  api.state = coordinator.state;
  const start = () => api.sync().catch(() => {
    if (latestCart?.items.some(isGift)) showMessage('Não foi possível ajustar o brinde. Confira o carrinho antes de finalizar.', true);
  }).finally(() => document.dispatchEvent(new CustomEvent('cp:outubro-rosa:ready')));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  window.addEventListener('pageshow', (event) => { if (event.persisted) start(); });
})();
